export interface OHLCVCandle {
  timestamp: number; // Unix ms
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface PredictionResult {
  timestamps: number[];
  median: number[];
  p10: number[];
  p25: number[];
  p75: number[];
  p90: number[];
  rise_probability: number;
  predicted_volatility: number;
  samples: number[][]; // Monte Carlo paths
}

export interface SymbolPrediction {
  symbol: string;
  prediction: PredictionResult;
  current_price: number;
  ohlcv: OHLCVCandle[];
}

export interface EnsembleSignal {
  timestamp: number;
  type: 'BUY' | 'SELL';
  price: number;
}

export interface EnsembleCloud {
  p10: number[];
  p25: number[];
  p75: number[];
  p90: number[];
}

export interface EnsembleResult {
  timestamps: number[];
  ensemble_median: number[];
  samples: number[][]; // 1h paths
  long_cloud: EnsembleCloud;
  signals: EnsembleSignal[];
  predicted_volatility: number;
  rise_probability: number;
}

export interface SymbolEnsemblePrediction {
  symbol: string;
  source: string;
  ensemble: EnsembleResult;
  current_price: number;
}

export interface Alert {
  id: string;
  symbol: string;
  type: 'rise_signal' | 'fall_signal' | 'volatility_spike' | 'ai_entry_signal';
  message: string;
  severity: 'info' | 'warning' | 'critical';
  timestamp: string;
  price: number;
}



export interface SymbolsResponse {
  crypto: string[];
  stock: string[];
  fx: string[];
}

export type MarketType = 'all' | 'crypto' | 'stock' | 'fx';
export type Interval = '1h' | '4h' | '1d';


// ── 市場レジーム ────────────────────────────────────────────────────────────

export type RegimeType = 'trending' | 'volatile' | 'ranging';

export interface RegimeResult {
  symbol:      string;
  regime:      RegimeType;
  direction:   'up' | 'down' | 'none';
  adx:         number;
  atr_ratio:   number;
  ema_aligned: boolean;
  description: string;
}

// ── テクニカル指標 ──────────────────────────────────────────────────────────

export interface IndicatorLatest {
  rsi:         number | null;
  macd:        number | null;
  macd_signal: number | null;
  bb_width:    number | null;
  atr:         number | null;
  stoch_k:     number | null;
  stoch_d:     number | null;
}

export interface IndicatorsResult {
  symbol:       string;
  interval:     string;
  timestamps:   number[];
  ema10:        (number | null)[];
  ema20:        (number | null)[];
  ema50:        (number | null)[];
  bb_upper:     (number | null)[];
  bb_mid:       (number | null)[];
  bb_lower:     (number | null)[];
  bb_width:     (number | null)[];
  rsi:          (number | null)[];
  macd:         (number | null)[];
  macd_signal:  (number | null)[];
  macd_hist:    (number | null)[];
  atr:          (number | null)[];
  stoch_k:      (number | null)[];
  stoch_d:      (number | null)[];
  obv:          (number | null)[];
  latest:       IndicatorLatest;
}

// ── 経済カレンダー ──────────────────────────────────────────────────────────

export interface EconomicEvent {
  id:          string;
  date_utc:    string | null;
  date_jst:    string | null;
  date_jst_str: string;
  currency:    string;
  currency_ja: string;
  impact:      'High' | 'Medium' | 'Low';
  impact_ja:   string;
  title:       string;
  title_ja:    string;
  forecast:    string;
  forecast_ja?: string;
  previous:    string;
  previous_ja?: string;
}

export interface CalendarResponse {
  events:   EconomicEvent[];
  imminent: EconomicEvent[];
  hours:    number;
}

// ── 通貨強度 ────────────────────────────────────────────────────────────────

export interface CurrencyStrengthEntry {
  currency: string;
  score:    number;
}

export interface CurrencyStrengthResult {
  scores:   Record<string, number>;
  rankings: CurrencyStrengthEntry[];
  period:   number;
}

// ── Accuracy Stats ────────────────────────────────────────────────────────

export interface AccuracyCalibrationBucket {
  bucket:      number;  // 0.0 〜 1.0 (0.1 刻み)
  label:       string;  // "0%" 〜 "100%"
  count:       number;
  actual_rate: number;  // 実際の上昇率 (%)
}

export interface AccuracyBySymbol {
  symbol:       string;
  total:        number;
  accuracy_pct: number;
}

export interface AccuracyByInterval {
  interval:     string;
  total:        number;
  accuracy_pct: number;
}

export interface AccuracyByDirection {
  direction:    'BUY' | 'SELL';
  total:        number;
  accuracy_pct: number;
}

export interface AccuracyRecord {
  id:           string;
  symbol:       string;
  interval:     string;
  recorded_at:  number;  // ms
  entry_price:  number;
  rise_prob:    number;
  pred_median:  number;
  pred_p10:     number;
  pred_p90:     number;
  actual_price: number;
  actual_rose:  number;  // 1 or 0
  resolved_at:  number;  // ms
}

export interface AccuracyStats {
  total:              number;
  pending:            number;
  correct:            number;
  dir_correct:        number;
  accuracy_pct:       number;
  dir_accuracy_pct:   number;
  avg_median_err_pct: number;
  coverage_pct:       number;
  calibration:        AccuracyCalibrationBucket[];
  by_symbol:          AccuracyBySymbol[];
  by_interval:        AccuracyByInterval[];
  by_direction:       AccuracyByDirection[];
  // 事後キャリブレーション(Platt)のシャドー表示。rise_prob_calがフィット後に
  // 記録された予測のみ対象(記録専用、ライブのBUY/SELL判定には未反映)。
  calibration_shadow: AccuracyCalibrationBucket[];
  shadow_total:       number;
  brier_raw:          number | null;
  brier_cal:          number | null;
  history:            AccuracyRecord[];
}

// ── 取引ログ（適応的BUY/SELLシグナルの模擬取引） ─────────────────────────────

export interface TradeLogRecord {
  id:           string;
  symbol:       string;
  interval:     string;
  direction:    'BUY' | 'SELL';
  recorded_at:  number;  // ms
  entry_price:  number;
  take_profit:  number;
  stop_loss:    number;
  rise_prob:    number;
  outcome:      'tp_hit' | 'sl_hit' | 'timeout_win' | 'timeout_loss';
  exit_price:   number;
  pnl_pct:      number;  // 0.03 = +3%
  resolved_at:  number;  // ms
}

// エントリー中(未解決)のシグナル。resolved_at到達までTP/SLどちらへ到達するか未確定。
export interface TradeLogPendingRecord {
  id:           string;
  symbol:       string;
  interval:     string;
  direction:    'BUY' | 'SELL';
  recorded_at:  number;  // ms
  target_at:    number;  // ms、この時刻を過ぎると期限判定される
  entry_price:  number;
  take_profit:  number;
  stop_loss:    number;
  rise_prob:    number;
}

export interface TradeLogBySymbol {
  symbol:       string;
  total:        number;
  win_rate_pct: number;
  avg_pnl_pct:  number;
}

export interface TradeLogByInterval {
  interval:     string;
  total:        number;
  win_rate_pct: number;
}

export interface TradeLogByDirection {
  direction:    'BUY' | 'SELL';
  total:        number;
  win_rate_pct: number;
  avg_pnl_pct:  number;
}

export interface TradeLogOutcomeBreakdown {
  tp_hit:       number;
  sl_hit:       number;
  timeout_win:  number;
  timeout_loss: number;
}

export interface TradeLogStats {
  total:              number;
  pending:            number;
  pending_trades:     TradeLogPendingRecord[];
  wins:               number;
  win_rate_pct:       number;
  avg_pnl_pct:        number;
  total_pnl_pct:      number;
  outcome_breakdown:  TradeLogOutcomeBreakdown;
  by_symbol:          TradeLogBySymbol[];
  by_interval:        TradeLogByInterval[];
  by_direction:       TradeLogByDirection[];
  history:            TradeLogRecord[];
}

// ── AI軍師 ───────────────────────────────────────────────────────────────────

// AI軍師(LLM)の自己申告的な推奨、および /api/trades/active-signal/{symbol} が返す
// 実際の売買シグナル(ADX/RSI/MACDルールフィルタ通過済み)の両方で同じ形を使う。
// win_probabilityは実シグナル側でサンプル不足時にnullになりうるためnullable。
export interface TradeRecommendation {
  recommend_entry: boolean;
  action: 'BUY' | 'SELL' | 'HOLD';
  entry_price: number | null;
  take_profit: number | null;
  stop_loss: number | null;
  win_probability: number | null;
  risk_reward_ratio: number | null;
  // 実シグナル側でのみ利用可能(ms epoch)。AI軍師の自己申告的な推奨には無い。
  recorded_at?: number | null;
  // 実シグナル側でのみ利用可能。entry_price(成行)からSL距離の一定割合だけ
  // 逆行(ドローダウン)した水準の指値目安。AI軍師の自己申告的な推奨には無い。
  suggested_entry_price?: number | null;
  // 実シグナル側でのみ利用可能。FX週末休場中・欧米長期休場中はtrueになる。
  // 休場中でも既存のアクティブシグナルは表示し、この値で市況を補足する。
  market_closed?: boolean;
}

export interface StrategistResult {
  symbol:                string;
  strategy_memo:         string;
  volatility_multiplier: number;
  bias_shift:            number;
  // AI軍師(LLM)自己申告のtrade_recommendationは、同一market_typeでのシャドー
  // 実績(interval="ai_strategist")がensembleの実績を安定して上回ると自動判定
  // (卒業)されるまでnull。バックエンド側のゲート(main.py /api/strategist/{symbol})
  // により、卒業前はtrade_recommendation自体がnullで届く。
  trade_recommendation?: TradeRecommendation | null;
  trade_recommendation_graduated?: boolean;
  updated_at:            number; // Unix s
  source:                string;
}

// ── センチメント ──────────────────────────────────────────────────────────────

export interface SentimentArticle {
  title:        string;
  summary:      string;
  url:          string;
  published_at: string;
  source:       string;
  score:        number;   // -1.0 〜 +1.0
  label:        'positive' | 'negative' | 'neutral';
}

export interface SentimentResult {
  symbol:        string;
  score:         number;   // -1.0 〜 +1.0（集計値）
  label:         'positive' | 'negative' | 'neutral';
  article_count: number;
  articles:      SentimentArticle[];
  updated_at:    number;   // Unix 秒
  engine:        'finbert' | 'unavailable';
}

// ── Symbol Names Mapping ──────────────────────────────────────────────────

const SYMBOL_NAMES: Record<string, string> = {
  // Crypto
  'BTCUSDT': 'ビットコイン (BTC)',
  'ETHUSDT': 'イーサリアム (ETH)',
  'SOLUSDT': 'ソラナ (SOL)',
  'BNBUSDT': 'バイナンスコイン (BNB)',
  'XRPUSDT': 'リップル (XRP)',
  // Stock
  'AAPL': 'アップル (Apple)',
  'TSLA': 'テスラ (Tesla)',
  'NVDA': 'エヌビディア (NVIDIA)',
  'MSFT': 'マイクロソフト (Microsoft)',
  'GOOGL': 'アルファベット (Google)',
  '7203.T': 'トヨタ自動車',
  '9984.T': 'ソフトバンクグループ',
  // Indices
  '^N225': '日経平均株価 (Nikkei 225)',
  '^GSPC': 'S&P 500 指数',
  // FX
  'USDJPY=X': '米ドル / 円 (USD/JPY)',
  'EURUSD=X': 'ユーロ / 米ドル (EUR/USD)',
  'GBPUSD=X': '英ポンド / 米ドル (GBP/USD)',
  'AUDUSD=X': '豪ドル / 米ドル (AUD/USD)',
  'USDCAD=X': '米ドル / カナダドル (USD/CAD)',
};

export function getSymbolName(symbol?: string | null): string {
  if (!symbol) return '';
  return SYMBOL_NAMES[symbol.toUpperCase()] || '';
}


// ── Currency Helpers ──────────────────────────────────────────────────────────

export function getCurrencySymbol(symbol?: string | null): string {
  if (!symbol) return '$';
  const s = symbol.toUpperCase();
  if (s.includes('JPY') || s.endsWith('.T') || s === '^N225' || s === '^TPX') return '¥';
  return '$';
}

export function formatPriceWithSymbol(price: number, symbol?: string | null): string {
  const s = symbol?.toUpperCase() ?? '';

  // FX pairs: fixed decimal places per trading convention
  // JPY pairs (USDJPY, EURJPY, etc.) → 3 decimal places
  // Other major pairs (EURUSD, GBPUSD, etc.) → 5 decimal places
  if (s.endsWith('=X')) {
    if (s.includes('JPY')) return `¥${price.toFixed(3)}`;
    return price.toFixed(5);
  }

  const currencySign = getCurrencySymbol(symbol);
  if (currencySign === '¥') {
    return `¥${price.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
  }
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(2)}M`;
  if (price >= 1_000) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price < 0.001) return `$${price.toFixed(6)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  // XRP等、1〜1000ドル帯の暗号資産はTP/SLの差が小数点以下に収まるため4桁表示にする
  const isCryptoSymbol = s.endsWith('USDT') || s.endsWith('BTC') || s.endsWith('ETH');
  if (isCryptoSymbol) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(2)}`;
}
